/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import './index.css';
import { supabase } from './supabase';
import Ic from './Icon';
import { $, fd, dl, td, uid, TIERS } from './theme';
import {
  TENANTS as DEMO_TENANTS, LEASES as DEMO_LEASES,
  INIT_MX, INIT_PAY, INIT_PROPS, INIT_MSGS, INIT_DOCS, INIT_VACANCIES
} from './data';
import { useSupabaseData } from './useSupabaseData';
import { sendPaymentReceipt, sendMaintenanceUpdate, sendLateNotice } from './useEmail';
import PayModal       from './components/PayModal';
import MxModal        from './components/MxModal';
import Messaging      from './components/Messaging';
import Documents      from './components/Documents';
import LateFees       from './components/LateFees';
import Receipts       from './components/Receipts';
import Checklists     from './components/Checklists';
import VacancyTracker from './components/VacancyTracker';
import LeaseRenewal   from './components/LeaseRenewal';
import BroadcastMsg   from './components/BroadcastMsg';
import NoticeToVacate from './components/NoticeToVacate';
import ExpenseTracker from './components/ExpenseTracker';
import ProGate        from './components/ProGate';
import PricingPage    from './components/PricingPage';
import VendorDirectory from './components/VendorDirectory';
import InstallPrompt  from './components/InstallPrompt';
import LandingPage    from './components/LandingPage';
import AuthScreen     from './components/AuthScreen';
import ResetPassword  from './components/ResetPassword';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy  from './components/PrivacyPolicy';
import RentPricing    from './components/RentPricing';
import AddPropertyModal from './components/AddPropertyModal';
import AddTenantModal   from './components/AddTenantModal';
import Badges from './components/Badges';

const { TB, MB, PB, MXS } = Badges;
const EG = 'linear-gradient(135deg,#16a34a,#4ade80)';

// ─────────────────────────────────────────────────────────────────────────────
// PROPERTY TRACKER
// ─────────────────────────────────────────────────────────────────────────────
function PropertyTracker({ props, mx, tenants, onEdit }) {
  const P_LIST = props || [];
  const [sel, setSel]       = useState(null);
  const [newVal, setNewVal] = useState('');
  const [editVal, setEditVal] = useState(false);
  const [addImp, setAddImp] = useState(false);
  const [imp, setImp]       = useState({ d:'', c:'', yr: new Date().getFullYear() });

  // Portfolio totals
  const totV = P_LIST.reduce((s,p) => s + (p.current_value || p.val || 0), 0);
  const totB = P_LIST.reduce((s,p) => s + (p.purchase_price || p.buy || 0), 0);
  const totE = P_LIST.reduce((s,p) => s + ((p.current_value||p.val||0) - (p.mortgage_balance||p.mb||0)), 0);
  const totM = P_LIST.reduce((s,p) => s + (p.monthly_payment || p.mp || 0), 0);

  // All maintenance items across all properties
  const allMxItems = P_LIST.flatMap(p =>
    (p.maintenance_schedule || p.mx || []).map(m => ({ ...m, pName: p.name }))
  );
  const urgent = allMxItems.filter(m => (m.status||m.st) === 'overdue' || (m.status||m.st) === 'due');

  const P = P_LIST.find(p => p.id === sel);

  const saveVal = () => {
    if (!newVal || !P) return;
    onEdit && onEdit({ ...P, current_value: parseFloat(newVal) || P.current_value });
    setEditVal(false);
    setNewVal('');
  };

  const saveImp = () => {
    if (!imp.d || !imp.c) return;
    onEdit && onEdit({
      ...P,
      improvements: [...(P.improvements || P.impr || []), { yr: parseInt(imp.yr), description: imp.d, d: imp.d, cost: parseFloat(imp.c), c: parseFloat(imp.c) }]
    });
    setAddImp(false);
    setImp({ d:'', c:'', yr: new Date().getFullYear() });
  };

  // ── Property Detail View ──────────────────────────────────────────────────
  if (P) {
    const pVal = P.current_value  || P.val  || 0;
    const pMb  = P.mortgage_balance || P.mb || 0;
    const pBuy = P.purchase_price || P.buy  || 0;
    const pMp  = P.monthly_payment || P.mp  || 0;
    const pMr  = P.interest_rate   || P.mr  || 0;
    const pImpr= P.improvements    || P.impr || [];
    const pMxSch = P.maintenance_schedule || P.mx || [];
    const eq   = pVal - pMb;
    const eqPct= pVal > 0 ? Math.round((eq / pVal) * 100) : 0;
    const gain = pVal - pBuy;
    const gPct = pBuy > 0 ? Math.round((gain / pBuy) * 100) : 0;
    const iT   = pImpr.reduce((s,i) => s + (i.cost || i.c || 0), 0);
    const tMx  = (mx||[]).filter(m => m.property_id === P.id || m.p === P.name);

    return (
      <div>
        <div style={{display:'flex',gap:10,marginBottom:20,alignItems:'center'}}>
          <button className="btn btn-gh" onClick={() => setSel(null)}>← All Properties</button>
          <div style={{flex:1}}/>
          <button className="btn btn-g" onClick={() => setEditVal(!editVal)}>
            <Ic n="edit" s={14}/>Update Value
          </button>
        </div>

        {editVal && (
          <div className="card" style={{marginBottom:16,border:'1px solid rgba(34,197,94,.2)'}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-end'}}>
              <div className="fi" style={{flex:1}}>
                <label>New Estimated Value ($)</label>
                <input value={newVal} onChange={e=>setNewVal(e.target.value)} placeholder="e.g. 510000" autoFocus/>
              </div>
              <button className="btn btn-g" onClick={saveVal}><Ic n="check" s={14}/>Save</button>
              <button className="btn btn-gh" onClick={() => setEditVal(false)}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{background:'linear-gradient(135deg,#1c2333,#21262d)',border:'1px solid #2d333b',borderRadius:15,padding:24,marginBottom:16,position:'relative',overflow:'hidden'}}>
          <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:EG}}/>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12,marginBottom:18}}>
            <div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:800,color:'#fff',marginBottom:4}}>{P.name}</div>
              <div style={{fontSize:12,color:'#8b949e',marginBottom:10}}>{P.address || P.addr}</div>
              <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
                <span className="b bgo">{P.type}</span>
                <span className="b bg">{P.units} unit{P.units>1?'s':''}</span>
                {(P.year_built||P.yr) && <span className="b bbl">Built {P.year_built||P.yr}</span>}
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:10,color:'#484f58',textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>Current Value</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:32,fontWeight:900,color:'#4ade80',letterSpacing:-1}}>{$(pVal)}</div>
              <div style={{fontSize:11,color:'#8b949e',marginTop:2}}>Updated {fd(P.value_updated_at||P.vd)}</div>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:14}}>
            {[
              {l:'Purchase',  v:$(pBuy),   s:fd(P.purchase_date||P.bd)},
              {l:'Appreciation',v:$(gain), s:gPct+'%'},
              {l:'Equity',    v:$(eq),     s:eqPct+'%'},
              {l:'Invested',  v:$(iT),     s:pImpr.length+' projects'},
            ].map((s,i) => (
              <div key={i}>
                <div style={{fontSize:10,color:'#484f58',textTransform:'uppercase',letterSpacing:1,fontWeight:600}}>{s.l}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:'#fff',margin:'3px 0'}}>{s.v}</div>
                <div style={{fontSize:11,color:'#8b949e'}}>{s.s}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#8b949e',marginBottom:5}}>
            <span>Mortgage: {$(pMb)}</span><span>Equity: {$(eq)} ({eqPct}%)</span>
          </div>
          <div style={{height:8,background:'#21262d',borderRadius:4,overflow:'hidden'}}>
            <div style={{height:'100%',width:eqPct+'%',background:EG,borderRadius:4}}/>
          </div>
        </div>

        <div className="g2" style={{marginBottom:16}}>
          <div className="ds">
            <div className="ds-hd"><Ic n="bank" s={15} c="#22c55e"/>Mortgage & Insurance</div>
            {[
              {l:'Lender',  v:P.lender||'—'},
              {l:'Monthly', v:$(pMp)},
              {l:'Rate',    v:pMr+'%'},
              {l:'Balance', v:$(pMb)},
              {l:'Insurer', v:P.insurance_carrier||P.ins||'—'},
              {l:'Tax/yr',  v:$(P.property_tax_annual||P.tx||0)},
            ].map((r,i) => (
              <div className="row" key={i}>
                <span className="rl">{r.l}</span>
                <span style={{fontSize:13,fontWeight:600}}>{r.v}</span>
              </div>
            ))}
          </div>
          <div className="ds">
            <div className="ds-hd"><Ic n="tool" s={15} c="#22c55e"/>Maintenance Schedule</div>
            {pMxSch.length === 0 && <div style={{fontSize:12,color:'#484f58',padding:'8px 0'}}>No scheduled maintenance</div>}
            {pMxSch.map((m,i) => {
              const st = m.status || m.st || 'ok';
              const cfg = MXS[st] || MXS.ok;
              return (
                <div className="mxi" key={i}>
                  <div className="mxd" style={{background:cfg.col}}/>
                  <div style={{flex:1}}>
                    <div className="mxt">{m.task}</div>
                    <div className="mxm">Due {fd(m.next_due||m.nd)}</div>
                  </div>
                  <span className={'b '+cfg.bc} style={{fontSize:9}}>{cfg.lbl}</span>
                </div>
              );
            })}
            {tMx.length > 0 && <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid #21262d',fontSize:12,color:'#d29922'}}>{tMx.length} open tenant request{tMx.length>1?'s':''}</div>}
          </div>
        </div>

        <div className="ds" style={{marginBottom:16}}>
          <div className="ds-hd" style={{justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:7}}><Ic n="trend" s={15} c="#22c55e"/>Capital Improvements</div>
            {!addImp && <button className="btn btn-d" style={{padding:'4px 10px',fontSize:11}} onClick={()=>setAddImp(true)}><Ic n="plus" s={12}/>Add</button>}
          </div>
          {addImp && (
            <div style={{background:'#21262d',borderRadius:8,padding:12,marginBottom:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div className="fi" style={{flex:2,minWidth:130}}>
                <label>Description</label>
                <input value={imp.d} onChange={e=>setImp({...imp,d:e.target.value})} placeholder="e.g. Kitchen renovation"/>
              </div>
              <div className="fi" style={{width:90}}>
                <label>Cost ($)</label>
                <input value={imp.c} onChange={e=>setImp({...imp,c:e.target.value})} placeholder="0"/>
              </div>
              <div className="fi" style={{width:75}}>
                <label>Year</label>
                <input value={imp.yr} onChange={e=>setImp({...imp,yr:e.target.value})}/>
              </div>
              <button className="btn btn-g" style={{padding:'6px 12px',fontSize:11}} onClick={saveImp}><Ic n="check" s={12}/>Save</button>
              <button className="btn btn-gh" style={{padding:'6px 12px',fontSize:11}} onClick={()=>setAddImp(false)}>Cancel</button>
            </div>
          )}
          {pImpr.map((im,i) => (
            <div className="impi" key={i}>
              <span className="impyr">{im.yr}</span>
              <span className="impd">{im.description||im.d}</span>
              <span className="impc">{$(im.cost||im.c)}</span>
            </div>
          ))}
          {pImpr.length === 0 && <div style={{fontSize:12,color:'#484f58',padding:'8px 0'}}>No improvements recorded yet.</div>}
          <div style={{paddingTop:11,display:'flex',justifyContent:'space-between',borderTop:'1px solid #21262d',marginTop:8}}>
            <span style={{fontSize:13,color:'#8b949e'}}>Total invested</span>
            <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:'#4ade80',fontSize:15}}>{$(iT)}</span>
          </div>
        </div>

        {P.notes && (
          <div className="ds">
            <div className="ds-hd"><Ic n="info" s={15} c="#22c55e"/>Notes</div>
            <div style={{fontSize:13,color:'#c9d1d9',lineHeight:1.7,background:'#21262d',borderRadius:8,padding:'11px 14px'}}>{P.notes}</div>
          </div>
        )}
      </div>
    );
  }

  // ── Portfolio List View ───────────────────────────────────────────────────
  return (
    <div>
      {urgent.length > 0 && (
        <div className="alert"><Ic n="alert" s={15}/><span><strong>{urgent.length}</strong> maintenance item{urgent.length>1?'s':''} need attention.</span></div>
      )}
      {P_LIST.length === 0 && (
        <div className="card" style={{textAlign:'center',padding:'44px 22px'}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:'#fff',marginBottom:8}}>No Properties Yet</div>
          <div style={{fontSize:13,color:'#484f58'}}>Click "Add Property" to add your first property.</div>
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:14}}>
        {P_LIST.map(p => {
          const pv = p.current_value||p.val||0;
          const pm = p.mortgage_balance||p.mb||0;
          const pb = p.purchase_price||p.buy||0;
          const eq = pv-pm;
          const eqP = pv>0 ? Math.round((eq/pv)*100) : 0;
          const gain = pv-pb;
          const gP = pb>0 ? Math.round((gain/pb)*100) : 0;
          const alerts = (p.maintenance_schedule||p.mx||[]).filter(m=>(m.status||m.st)==='overdue'||(m.status||m.st)==='due').length;
          const tenantCount = (tenants||[]).filter(t=>t.property_id===p.id).length;
          return (
            <div className="pc" key={p.id} onClick={()=>setSel(p.id)}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12,marginBottom:14}}>
                <div>
                  <h3>{p.name}</h3>
                  <div className="pc-addr">{p.address||p.addr}</div>
                  <div style={{display:'flex',gap:7,flexWrap:'wrap',marginTop:6}}>
                    <span className="b bgo">{p.type||'Property'}</span>
                    <span className="b bg">{p.units||1} unit{(p.units||1)>1?'s':''}</span>
                    {alerts>0 && <span className="b br">{alerts} alert{alerts>1?'s':''}</span>}
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:10,color:'#484f58',textTransform:'uppercase',letterSpacing:1,marginBottom:3}}>Est. Value</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:'#4ade80',letterSpacing:-1}}>{$(pv)}</div>
                  <div style={{fontSize:12,fontWeight:600,color:gain>=0?'#22c55e':'#f85149',marginTop:3}}>{gain>=0?'+':''}{$(gain)} ({gP}%)</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
                {[
                  {l:'Purchase',v:$(pb)},
                  {l:'Equity',  v:$(eq)+' ('+eqP+'%)'},
                  {l:'Mortgage',v:$(pm)},
                  {l:'Tenants', v:tenantCount},
                ].map((s,i)=>(
                  <div key={i}>
                    <div style={{fontSize:10,color:'#484f58',textTransform:'uppercase',letterSpacing:1}}>{s.l}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:13,fontWeight:700,color:'#fff',marginTop:3}}>{s.v}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#8b949e',marginBottom:5}}>
                <span>Mortgage: {$(pm)}</span><span>Equity: {$(eq)} ({eqP}%)</span>
              </div>
              <div style={{height:6,background:'#21262d',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:eqP+'%',background:EG,borderRadius:3}}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LANDLORD DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function LLDash({ setTab, pays, mx, tenants, leases }) {
  const T = tenants || [];
  const L = leases  || [];
  const P = pays    || [];
  const M = mx      || [];
  const tot    = T.reduce((s,t) => s + (t.monthly_rent||t.rent||0), 0);
  const col    = P.filter(p => (p.status||p.st)==='paid').reduce((s,p) => s+(p.amount||p.amt||0), 0);
  const omx    = M.filter(m => (m.status||m.st)==='open').length;
  const late   = T.filter(t => (t.status||t.st)==='late').length;
  const hiPriMx= M.filter(m => (m.priority||m.pri)==='high' && (m.status||m.st)==='open').length;

  return (
    <div>
      {late > 0 && (
        <div className="alert">
          <Ic n="alert" s={15}/>
          {late} tenant{late>1?'s are':' is'} past due.
          <button className="ca" style={{marginLeft:8}} onClick={()=>setTab('tenants')}>View →</button>
        </div>
      )}
      <div className="sg">
        {[
          {l:'Monthly Rent Roll', v:$(tot),  s:T.length+' tenants',       bar:'#22c55e'},
          {l:'Collected',         v:$(col),  s:tot>0?Math.round((col/tot)*100)+'% rate':'0% rate', bar:'#22c55e'},
          {l:'Open Maintenance',  v:omx,     s:hiPriMx+' high priority',  bar:'#d29922'},
          {l:'Late Tenants',      v:late,    s:'Need attention',           bar:'#f85149'},
        ].map((s,i) => (
          <div className="st" key={i}>
            <div className="stb" style={{background:s.bar}}/>
            <small>{s.l}</small>
            <div className="stv">{s.v}</div>
            <div className="sts">{s.s}</div>
          </div>
        ))}
      </div>

      <div className="g31" style={{marginBottom:18}}>
        <div className="card">
          <div className="card-hd">
            <h3>Open Maintenance</h3>
            <button className="ca" onClick={()=>setTab('maint')}>View all →</button>
          </div>
          {M.filter(m=>(m.status||m.st)!=='resolved').slice(0,5).map(m => (
            <div className="row" key={m.id}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>{m.title}</div>
                <div style={{fontSize:11,color:'#8b949e'}}>
                  {m.unit||m.u||'—'} · {m.property||m.p||'—'}
                </div>
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3}}>
                <MB s={m.status||m.st||'open'}/>
                <span style={{fontSize:10,color:'#484f58'}}>{fd(m.submitted_at||m.date||m.created_at)}</span>
              </div>
            </div>
          ))}
          {M.filter(m=>(m.status||m.st)!=='resolved').length===0 && (
            <div style={{textAlign:'center',color:'#484f58',padding:'18px 0',fontSize:13}}>No open requests!</div>
          )}
        </div>

        <div className="card">
          <div className="card-hd"><h3>Rent Status</h3></div>
          {P.slice(0,6).map(p => (
            <div className="row" key={p.id}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>{p.tenant_name||p.ten||'Tenant'}</div>
                <div style={{fontSize:11,color:'#8b949e'}}>Unit {p.unit||p.u||'—'}</div>
              </div>
              <PB s={p.status||p.st||'unpaid'}/>
            </div>
          ))}
          {P.length===0 && <div style={{textAlign:'center',color:'#484f58',padding:'18px 0',fontSize:13}}>No payments yet.</div>}
        </div>
      </div>

      <div className="g2">
        <div className="card">
          <div className="card-hd">
            <h3>Expiring Leases</h3>
            <button className="ca" onClick={()=>setTab('leases')}>View all →</button>
          </div>
          {L.length === 0 ? (
            <div style={{fontSize:13,color:'#484f58',textAlign:'center',padding:'20px 0'}}>No leases yet — add a tenant to get started.</div>
          ) : (
            L.filter(l => { const e = l.end_date||l.e; return e && dl(e) < 200; })
             .sort((a,b) => dl(a.end_date||a.e) - dl(b.end_date||b.e))
             .map(l => {
               const e    = l.end_date || l.e;
               const name = l.ten || (T.find(t=>t.id===l.tenant_id)||{}).full_name || 'Tenant';
               const unit = l.unit || l.u || '—';
               const prop = l.property || l.p || '—';
               const days = dl(e);
               return (
                 <div className="row" key={l.id}>
                   <div>
                     <div style={{fontSize:13,fontWeight:500}}>{name}</div>
                     <div style={{fontSize:11,color:'#8b949e'}}>{unit} · {prop}</div>
                   </div>
                   <span className={'b '+(days<60?'br':'by')}>{days}d left</span>
                 </div>
               );
             })
          )}
        </div>

        <div className="card">
          <div className="card-hd"><h3>Monthly Summary</h3></div>
          {[
            {l:'Gross Rent',  v:$(tot), c:'#22c55e'},
            {l:'Collected',   v:$(col), c:'#22c55e'},
            {l:'Uncollected', v:$(tot-col), c:'#f85149'},
          ].map((r,i) => (
            <div className="row" key={i}>
              <span className="rl">{r.l}</span>
              <span style={{fontSize:14,fontWeight:700,color:r.c}}>{r.v}</span>
            </div>
          ))}
          <div style={{background:'#21262d',borderRadius:9,padding:'12px 14px',marginTop:11}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700,fontSize:14}}>Net Income</span>
              <span style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:22,color:'#4ade80'}}>{$(col)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LANDLORD TENANTS
// ─────────────────────────────────────────────────────────────────────────────
function LLTenants({ pays, setPay, tenants, leases, onEdit }) {
  const T = tenants || [];
  const [f, sf] = useState('all');
  const [q, sq] = useState('');

  const list = T
    .filter(t => f==='all' || (t.status||t.st||'current')===f)
    .filter(t => {
      const n = (t.full_name||t.name||'').toLowerCase();
      const u = (t.unit||t.u||'');
      return n.includes(q.toLowerCase()) || u.includes(q);
    });

  return (
    <div>
      <div className="tabs">
        {[['all','All'],['current','Current'],['late','Late'],['notice','Notice'],['former','Former']].map(([v,l]) => (
          <button key={v} className={'tab'+(f===v?' tab-on':'')} onClick={()=>sf(v)}>{l}</button>
        ))}
      </div>
      <input className="srch" placeholder="Search tenants..." value={q} onChange={e=>sq(e.target.value)}/>
      {list.length === 0 ? (
        <div className="card" style={{textAlign:'center',padding:'40px 20px'}}>
          <div style={{fontSize:13,color:'#484f58'}}>No tenants found.</div>
        </div>
      ) : (
        <div className="card">
          <div className="tw">
            <table>
              <thead>
                <tr><th>Tenant</th><th>Unit</th><th>Rent</th><th>Balance</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {list.map(t => {
                  const nm   = t.full_name || t.name || '';
                  const unit = t.unit || t.u || '—';
                  const rent = t.monthly_rent || t.rent || 0;
                  const bal  = t.balance_due || t.bal || 0;
                  const st   = t.status || t.st || 'current';
                  const initials = nm.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="tc">
                          <div className="av">{t.avatar_initials || t.av || initials}</div>
                          <div>
                            <div style={{fontWeight:500}}>{nm}</div>
                            <div style={{fontSize:11,color:'#484f58'}}>{t.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{fontSize:13}}>{unit}</td>
                      <td style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:'#4ade80'}}>{$(rent)}</td>
                      <td style={{fontSize:13,fontWeight:700,color:bal>0?'#f85149':'#22c55e'}}>{bal>0?'-'+$(bal):'Current'}</td>
                      <td><TB s={st}/></td>
                      <td style={{display:'flex',gap:6}}>
                        <button className="btn btn-d" style={{padding:'5px 11px',fontSize:11}} onClick={()=>setPay(t)}>Pay</button>
                        <button className="btn btn-gh" style={{padding:'5px 11px',fontSize:11}} onClick={()=>onEdit&&onEdit(t)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LANDLORD LEASES
// ─────────────────────────────────────────────────────────────────────────────
function LLLeases({ leases, tenants }) {
  const L = leases  || [];
  const T = tenants || [];
  const [f, sf] = useState('all');

  const list = L.filter(l => f==='all' || (l.status||l.st||'active')===f);

  if (L.length === 0) return (
    <div className="card" style={{textAlign:'center',padding:'44px 22px'}}>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:'#fff',marginBottom:8}}>No Leases Yet</div>
      <div style={{fontSize:13,color:'#484f58'}}>Add a tenant with lease details to get started.</div>
    </div>
  );

  return (
    <div>
      <div className="tabs">
        {[['all','All'],['active','Active'],['expiring','Expiring'],['expired','Expired']].map(([v,l]) => (
          <button key={v} className={'tab'+(f===v?' tab-on':'')} onClick={()=>sf(v)}>{l}</button>
        ))}
      </div>
      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr><th>Tenant</th><th>Property / Unit</th><th>Term</th><th>Rent</th><th>Deposit</th><th>Days Left</th><th>Status</th></tr>
            </thead>
            <tbody>
              {list.map(l => {
                const t    = T.find(t => t.id === (l.tenant_id||l.tid)) || {};
                const nm   = t.full_name || t.name || l.ten || '—';
                const init = nm.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
                const unit = l.unit || l.u || t.unit || '—';
                const prop = l.property || l.p || '—';
                const rent = l.monthly_rent || l.rent || 0;
                const dep  = l.security_deposit || l.dep || 0;
                const sd   = l.start_date || l.s || '';
                const ed   = l.end_date   || l.e || '';
                const st   = l.status     || l.st || 'active';
                const days = dl(ed);
                return (
                  <tr key={l.id}>
                    <td>
                      <div className="tc">
                        <div className="av" style={{width:28,height:28,fontSize:10}}>{init}</div>
                        <span style={{fontWeight:500}}>{nm}</span>
                      </div>
                    </td>
                    <td><div style={{fontSize:13}}>{prop}</div><div style={{fontSize:11,color:'#8b949e'}}>Unit {unit}</div></td>
                    <td style={{fontSize:12,color:'#8b949e'}}><div>{fd(sd)}</div><div>→ {fd(ed)}</div></td>
                    <td style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:'#4ade80'}}>{$(rent)}</td>
                    <td style={{fontSize:13,color:'#8b949e'}}>{$(dep)}</td>
                    <td><div style={{fontSize:13,fontWeight:700,color:days<60?'#f85149':days<120?'#d29922':'#f5f0e8'}}>{days}d</div></td>
                    <td><span className={'b '+(st==='active'?'bg':st==='expiring'?'by':'br')}>{st}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LANDLORD MAINTENANCE
// ─────────────────────────────────────────────────────────────────────────────
function LLMaint({ mx, onStatusUpdate }) {
  const M = mx || [];
  const [f, sf] = useState('all');

  const list = M.filter(m => f==='all' || (m.status||m.st||'open')===f);

  const updateStatus = (id, newStatus) => {
    onStatusUpdate && onStatusUpdate(id, newStatus);
  };

  return (
    <div>
      <div className="tabs">
        {[['all','All'],['open','Open'],['in_progress','In Progress'],['resolved','Resolved']].map(([v,l]) => (
          <button key={v} className={'tab'+(f===v?' tab-on':'')} onClick={()=>sf(v)}>{l}</button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="card" style={{textAlign:'center',padding:'32px'}}>
          <div style={{fontSize:13,color:'#484f58'}}>No maintenance requests.</div>
        </div>
      ) : (
        <div className="card">
          <div className="tw">
            <table>
              <thead>
                <tr><th>Issue</th><th>Location</th><th>Tenant</th><th>Priority</th><th>Date</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {list.map(m => {
                  const st  = m.status || m.st || 'open';
                  const pri = m.priority || m.pri || 'medium';
                  return (
                    <tr key={m.id}>
                      <td style={{fontWeight:500,maxWidth:200}}>
                        {m.title}
                        <div style={{fontSize:11,color:'#484f58',marginTop:2,fontWeight:400}}>{m.description||m.notes}</div>
                      </td>
                      <td>
                        <div style={{fontSize:13}}>Unit {m.unit||m.u||'—'}</div>
                        <div style={{fontSize:11,color:'#8b949e'}}>{m.property||m.p||'—'}</div>
                      </td>
                      <td style={{fontSize:13}}>{m.tenant_name||m.ten||'—'}</td>
                      <td>
                        <span style={{fontWeight:700,fontSize:12,textTransform:'capitalize',color:pri==='high'?'#f85149':pri==='medium'?'#d29922':'#22c55e'}}>{pri}</span>
                      </td>
                      <td style={{fontSize:12,color:'#484f58'}}>{fd(m.submitted_at||m.date||m.created_at)}</td>
                      <td><MB s={st}/></td>
                      <td>
                        {st==='open'       && <button className="btn btn-d"  style={{padding:'4px 10px',fontSize:11}} onClick={()=>updateStatus(m.id,'in_progress')}>Start</button>}
                        {st==='in_progress'&& <button className="btn btn-ok" style={{padding:'4px 10px',fontSize:11}} onClick={()=>updateStatus(m.id,'resolved')}>Resolve</button>}
                        {st==='resolved'   && <span style={{fontSize:12,color:'#22c55e',display:'flex',alignItems:'center',gap:4}}><Ic n="check" s={12} c="#22c55e"/>Done</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LANDLORD FINANCIALS
// ─────────────────────────────────────────────────────────────────────────────
function LLFin({ pays }) {
  const P = pays || [];
  const [f, sf] = useState('all');
  const inc = P.filter(p=>(p.status||p.st)==='paid').reduce((s,p)=>s+(p.amount||p.amt||0),0);

  return (
    <div>
      <div className="sg3">
        {[
          {l:'Income',  v:$(inc), bar:'#22c55e'},
          {l:'Uncollected', v:$(P.filter(p=>(p.status||p.st)!=='paid').reduce((s,p)=>s+(p.amount||p.amt||0),0)), bar:'#f85149'},
          {l:'Payments', v:P.length, bar:'#58a6ff'},
        ].map((s,i) => (
          <div className="st" key={i}>
            <div className="stb" style={{background:s.bar}}/>
            <small>{s.l}</small>
            <div className="stv">{s.v}</div>
          </div>
        ))}
      </div>
      <div className="tabs">
        {[['all','All'],['income','Income'],['unpaid','Unpaid']].map(([v,l]) => (
          <button key={v} className={'tab'+(f===v?' tab-on':'')} onClick={()=>sf(v)}>{l}</button>
        ))}
      </div>
      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr><th>Date</th><th>Tenant</th><th>Amount</th><th>Method</th><th>Reference</th><th>Status</th></tr>
            </thead>
            <tbody>
              {P.filter(p => {
                if(f==='all') return true;
                if(f==='income') return (p.status||p.st)==='paid';
                if(f==='unpaid') return (p.status||p.st)!=='paid';
                return true;
              }).map(p => (
                <tr key={p.id}>
                  <td style={{fontSize:12,color:'#484f58'}}>{fd(p.payment_date||p.date)}</td>
                  <td style={{fontSize:13,fontWeight:500}}>{p.tenant_name||p.ten||'—'}</td>
                  <td style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:(p.status||p.st)==='paid'?'#22c55e':'#f85149'}}>
                    {$(p.amount||p.amt||0)}
                  </td>
                  <td style={{fontSize:12}}>{p.method||p.mth||'—'}</td>
                  <td style={{fontSize:11,color:'#8b949e'}}>{p.reference_no||p.ref||'—'}</td>
                  <td><PB s={p.status||p.st||'unpaid'}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          {P.length===0 && <div style={{textAlign:'center',padding:'24px',color:'#484f58',fontSize:13}}>No payments yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TENANT VIEWS
// ─────────────────────────────────────────────────────────────────────────────
function THome({ t, pays, onPay, leases }) {
  const tid = t.id;
  const myPays = (pays||[]).filter(p => p.tenant_id===tid || p.tid===tid);
  const last   = myPays[0];
  const paid   = last && ((last.status||last.st)==='paid');
  const lz     = (leases||[]).find(l => l.tenant_id===tid || l.tid===tid);
  const days   = lz ? dl(lz.end_date||lz.e) : 0;
  const rent   = t.monthly_rent || t.rent || 0;

  return (
    <div>
      <div className="thero">
        <h2>Welcome back, {(t.full_name||t.name||'').split(' ')[0]}.</h2>
        <p>{t.property||t.p||''} · Unit {t.unit||t.u||''}</p>
        <div className="thero-row">
          <div className="ths"><small>Monthly Rent</small><div className="thv">{$(rent)}</div></div>
          <div className="ths"><small>Lease Ends</small><div className="thv" style={{fontSize:15}}>{lz?fd(lz.end_date||lz.e):'—'}</div></div>
          <div className="ths"><small>Days Left</small><div className="thv" style={{color:days<90?'#d29922':'#4ade80'}}>{days}</div></div>
        </div>
      </div>
      <div className="rcrd">
        <div className="rcrd-top">
          <div>
            <small>Current Month Rent</small>
            <div className="rcrd-amt">{$(rent)}</div>
          </div>
          {paid
            ? <div className="rcpd"><Ic n="check" s={13} c="#22c55e"/>Paid</div>
            : <div className="rcow"><Ic n="alert" s={13} c="#f85149"/>Due Now</div>
          }
        </div>
        {!paid && <button className="btn btn-g" style={{marginTop:18,fontSize:14,padding:'11px 28px'}} onClick={onPay}><Ic n="dollar" s={15}/>Pay Now</button>}
        {paid && last && <div style={{marginTop:12,fontSize:13,color:'#8b949e'}}>Paid {fd(last.payment_date||last.date)} · Ref: <strong style={{color:'#86efac'}}>{last.reference_no||last.ref||'—'}</strong></div>}
      </div>
      <div className="g2">
        <div className="card">
          <div className="card-hd"><h3>Payment History</h3></div>
          {myPays.length===0
            ? <div style={{textAlign:'center',color:'#484f58',padding:'18px 0',fontSize:13}}>No payments yet.</div>
            : myPays.map((p,i) => (
                <div className="row" key={i}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500}}>{fd(p.payment_date||p.date)}</div>
                    <div style={{fontSize:11,color:'#8b949e'}}>{p.method||p.mth||'—'} · {p.reference_no||p.ref||'—'}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:'#4ade80'}}>{$(p.amount||p.amt||0)}</div>
                    <PB s={p.status||p.st||'unpaid'}/>
                  </div>
                </div>
              ))
          }
        </div>
        <div className="card">
          <div className="card-hd"><h3>Lease Details</h3></div>
          {lz ? [
            {l:'Property', v:lz.property||lz.p||'—'},
            {l:'Unit',     v:lz.unit||lz.u||'—'},
            {l:'Start',    v:fd(lz.start_date||lz.s)},
            {l:'End',      v:fd(lz.end_date||lz.e)},
            {l:'Rent',     v:$(lz.monthly_rent||lz.rent||0)},
            {l:'Deposit',  v:$(lz.security_deposit||lz.dep||0)},
          ].map((r,i)=>(
            <div className="row" key={i}>
              <span className="rl">{r.l}</span>
              <span style={{fontSize:13,fontWeight:600}}>{r.v}</span>
            </div>
          )) : <div style={{color:'#484f58',fontSize:13}}>No lease on file.</div>}
        </div>
      </div>
    </div>
  );
}

function TPays({ t, pays, onPay }) {
  const myPays = (pays||[]).filter(p => p.tenant_id===t.id || p.tid===t.id);
  const tot    = myPays.filter(p=>(p.status||p.st)==='paid').reduce((s,p)=>s+(p.amount||p.amt||0),0);
  const rent   = t.monthly_rent || t.rent || 0;
  const bal    = t.balance_due  || t.bal  || 0;
  return (
    <div>
      <div className="sg3">
        {[
          {l:'Total Paid YTD', v:$(tot),  bar:'#22c55e'},
          {l:'Monthly Rent',   v:$(rent), bar:'#58a6ff'},
          {l:'Balance Due',    v:$(bal),  bar:bal>0?'#f85149':'#22c55e'},
        ].map((s,i)=>(
          <div className="st" key={i}>
            <div className="stb" style={{background:s.bar}}/>
            <small>{s.l}</small>
            <div className="stv">{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
        <button className="btn btn-g" onClick={onPay}><Ic n="dollar" s={14}/>Make a Payment</button>
      </div>
      <div className="card">
        <div className="card-hd"><h3>Payment History</h3></div>
        {myPays.length===0
          ? <div style={{textAlign:'center',color:'#484f58',padding:'24px',fontSize:13}}>No payments yet.</div>
          : <div className="tw">
              <table>
                <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Status</th></tr></thead>
                <tbody>
                  {myPays.map((p,i)=>(
                    <tr key={i}>
                      <td>{fd(p.payment_date||p.date)}</td>
                      <td style={{fontFamily:"'Playfair Display',serif",fontWeight:700,color:'#4ade80'}}>{$(p.amount||p.amt||0)}</td>
                      <td>{p.method||p.mth||'—'}</td>
                      <td style={{fontSize:11,color:'#8b949e'}}>{p.reference_no||p.ref||'—'}</td>
                      <td><PB s={p.status||p.st||'unpaid'}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        }
      </div>
    </div>
  );
}

function TMaint({ t, mx, onNew }) {
  const myMx = (mx||[]).filter(m => m.tenant_id===t.id || m.tid===t.id);
  const [sel, setSel] = useState(null);
  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontSize:13,color:'#8b949e'}}>{myMx.length} request{myMx.length!==1?'s':''}</span>
        <button className="btn btn-g" onClick={onNew}><Ic n="plus" s={14}/>New Request</button>
      </div>
      {myMx.length===0 && (
        <div className="card" style={{textAlign:'center',padding:'44px 22px'}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:'#fff',marginBottom:7}}>No requests yet</div>
          <div style={{fontSize:13,color:'#8b949e',marginBottom:16}}>Submit a maintenance request and we'll handle it promptly.</div>
          <button className="btn btn-g" onClick={onNew}><Ic n="plus" s={14}/>Submit Request</button>
        </div>
      )}
      {myMx.map(m => {
        const st  = m.status || m.st || 'open';
        const pri = m.priority || m.pri || 'medium';
        const updates = m.maintenance_updates || m.upd || [];
        return (
          <div className="card" key={m.id} style={{marginBottom:11,cursor:'pointer'}} onClick={()=>setSel(sel===m.id?null:m.id)}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:11}}>
              <div>
                <div style={{fontWeight:600,fontSize:14,color:'#fff',marginBottom:3}}>{m.title}</div>
                <div style={{fontSize:12,color:'#8b949e'}}>{m.category||m.cat||'—'} · {fd(m.submitted_at||m.date||m.created_at)}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:5,flexShrink:0}}>
                <MB s={st}/>
                <span style={{fontSize:11,fontWeight:600,textTransform:'capitalize',color:pri==='high'?'#f85149':pri==='medium'?'#d29922':'#22c55e'}}>{pri}</span>
              </div>
            </div>
            {sel===m.id && updates.length>0 && (
              <div style={{marginTop:14,paddingTop:14,borderTop:'1px solid #21262d'}}>
                {updates.map((u,i)=>(
                  <div key={i} style={{display:'flex',gap:10,marginBottom:8}}>
                    <div style={{fontSize:11,color:'#484f58',whiteSpace:'nowrap'}}>{fd(u.d||u.update_date)}</div>
                    <div style={{fontSize:12,color:'#c9d1d9'}}>{u.m||u.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NAVIGATION CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const LL_NAV = [
  { id:'dash',     l:'Dashboard',        ic:'home',    pro:false },
  { id:'props',    l:'Property Tracker', ic:'build',   pro:true  },
  { id:'tenants',  l:'Tenants',          ic:'users',   pro:false },
  { id:'leases',   l:'Leases',           ic:'file',    pro:false },
  { id:'maint',    l:'Maintenance',      ic:'tool',    pro:false },
  { id:'fin',           l:'Financials',           ic:'dollar',  pro:false },
  { id:'pricing_intel', l:'Rent Pricing',         ic:'trend',   pro:true  },
  { id:'expense',  l:'Expenses',         ic:'receipt', pro:false },
  { id:'msgs',     l:'Messages',         ic:'msg',     pro:false },
  { id:'broadcast',l:'Broadcast',        ic:'send',    pro:true  },
  { id:'docs',     l:'Documents',        ic:'doc',     pro:false },
  { id:'fees',     l:'Late Fees',        ic:'alert',   pro:false },
  { id:'receipts', l:'Receipts',         ic:'receipt', pro:false },
  { id:'checks',   l:'Checklists',       ic:'move',    pro:false },
  { id:'vacancy',  l:'Vacancy Tracker',  ic:'build',   pro:true  },
  { id:'renewal',  l:'Lease Renewal',    ic:'file',    pro:true  },
  { id:'ntv',      l:'Notice to Vacate', ic:'alert',   pro:true  },
  { id:'pricing',  l:'Plans & Pricing',  ic:'crown',   pro:false },
  { id:'vendors',  l:'DFW Vendors',      ic:'users',   pro:false },
];
const T_NAV = [
  { id:'home',     l:'My Home',      ic:'home'    },
  { id:'pays',     l:'Payments',     ic:'dollar'  },
  { id:'maint',    l:'Maintenance',  ic:'tool'    },
  { id:'msgs',     l:'Messages',     ic:'msg'     },
  { id:'docs',     l:'Documents',    ic:'doc'     },
  { id:'receipts', l:'Receipts',     ic:'receipt' },
  { id:'checks',   l:'Inspections',  ic:'move'    },
];
const TITLES = {
  dash:'Dashboard',props:'Property Tracker',tenants:'Tenant Management',leases:'Lease Tracking',
  maint:'Maintenance',fin:'Financials',expense:'Expense Tracker',msgs:'Messages',
  broadcast:'Broadcast Message',docs:'Document Vault',fees:'Late Fee Calculator',
  receipts:'Rent Receipts',checks:'Checklists',vacancy:'Vacancy Tracker',
  renewal:'Lease Renewal',ntv:'Notice to Vacate',pricing:'Plans & Pricing',vendors:'DFW Vendor Network',
  pricing_intel:'Rent Pricing Intelligence',
};

// ─────────────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [portal,      setPortal]      = useState(null);
  const [demoMode,    setDemoMode]    = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [showAuth,    setShowAuth]    = useState(false);
  const [session,     setSession]     = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tenant,      setTenant]      = useState(null);
  const [tab,         setTab]         = useState('dash');
  const [sbOpen,      setSbOpen]      = useState(false);
  const [payMod,      setPayMod]      = useState(null);
  const [mxMod,       setMxMod]       = useState(false);
  const [showAddProp, setShowAddProp] = useState(false);
  const [editProp,    setEditProp]    = useState(null);
  const [showAddTen,  setShowAddTen]  = useState(false);
  const [editTen,     setEditTen]     = useState(null);
  const [showReset,   setShowReset]   = useState(false);
  const [showTerms,   setShowTerms]   = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // ── Auth ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setAuthLoading(false);
      if (s) { setDemoMode(false); }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY') { setShowReset(true); return; }
      setSession(s);
      if (s) { setDemoMode(false); setShowAuth(false); setShowReset(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Supabase data (only loads when logged in and not in demo) ─────────────
  const db = useSupabaseData(!demoMode && session ? session : null);

  // ── Tier: sourced from DB for real sessions; fixed 'pro' for demo ─────────
  // Demo mode shows all features so prospects see full value.
  // Real sessions read from the subscriptions table — never trust client state.
  const tier = demoMode ? 'pro' : db.tier;

  // ── Unified data sources ──────────────────────────────────────────────────
  const TENANTS = demoMode ? DEMO_TENANTS         : (db.tenants    || []);
  const LEASES  = demoMode ? DEMO_LEASES          : (db.leases     || []);
  const PROPS   = demoMode ? INIT_PROPS           : (db.properties || []);
  const PAYS    = demoMode ? INIT_PAY             : (db.payments   || []);
  const MX      = demoMode ? INIT_MX              : (db.mxRequests || []);
  const MSGS    = demoMode ? INIT_MSGS            : (db.messages   || []);
  const DOCS    = demoMode ? INIT_DOCS            : (db.documents  || []);
  const VACS      = demoMode ? INIT_VACANCIES     : (db.vacancies  || []);
  const EXPENSES  = demoMode ? []                 : (db.expenses   || []);
  const LATE_FEES = demoMode ? []                 : (db.lateFees   || []);
  const CHECKS    = demoMode ? []                 : (db.checklists || []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const go = p => { setPortal(p); setTab(p==='ll'?'dash':'home'); setSbOpen(false); };

  const handleSignOut = async () => {
    if (!demoMode) await supabase.auth.signOut();
    setSession(null); setPortal(null); setTenant(null);
    setDemoMode(false); setShowLanding(true); setShowAuth(false);
  };

  const handleMxStatusUpdate = (id, newStatus) => {
    if (!demoMode) {
      db.updateMxRequest(id, { status: newStatus });
      const req = MX.find(m => m.id === id);
      if (req) {
        const t = TENANTS.find(x => x.id === (req.tenant_id || req.tid));
        if (t?.email) sendMaintenanceUpdate({ tenantEmail: t.email, tenantName: t.full_name || t.name, request: req, newStatus });
      }
    }
  };

  const handleSaveProperty = async data => {
    if (!demoMode) {
      if (editProp) await db.updateProperty(editProp.id, data);
      else await db.addProperty(data);
    }
    setShowAddProp(false); setEditProp(null);
  };

  const handleSaveTenant = async (tenantData, leaseData) => {
    if (!demoMode) {
      if (editTen) {
        const result = await db.updateTenant(editTen.id, tenantData);
        if (result.error) throw new Error(result.error.message || 'Failed to update tenant.');
      } else {
        const result = await db.addTenant(tenantData);
        if (result.error) throw new Error(result.error.message || 'Failed to add tenant.');
        if (result.data && leaseData) {
          const leaseResult = await db.addLease({ ...leaseData, tenant_id: result.data.id });
          if (leaseResult.error) throw new Error(leaseResult.error.message || 'Tenant saved but lease could not be created.');
        }
      }
    }
    setShowAddTen(false); setEditTen(null);
  };

  const isLL      = portal === 'll';
  const isPro     = tier === 'pro' || tier === 'portfolio';
  const nav       = isLL ? LL_NAV : T_NAV;
  const tierInfo  = TIERS[tier];
  const omx       = MX.filter(m => (m.status||m.st)==='open').length;
  const unread    = MSGS.filter(m => (m.sender_role||m.from)==='tenant' && !m.read).length;
  const gateCheck = id => isLL && !isPro && (LL_NAV.find(n=>n.id===id)?.pro || false);

  // ── Loading Screen ────────────────────────────────────────────────────────
  const LoadingScreen = () => (
    <div style={{minHeight:'100vh',background:'#0e1117',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <svg width="44" height="44" viewBox="0 0 64 64" fill="none">
        <path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/>
        <rect x="10" y="50" width="44" height="6" rx="3" fill="#22c55e"/>
        <circle cx="32" cy="13" r="4" fill="#86efac"/>
      </svg>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:900,color:'#fff'}}>
        Rent<span style={{color:'#22c55e'}}>Lord</span>
      </div>
    </div>
  );

  if (authLoading) return <LoadingScreen/>;

  const termsModals = (
    <>
      {showTerms   && <TermsOfService onClose={() => setShowTerms(false)}/>}
      {showPrivacy && <PrivacyPolicy  onClose={() => setShowPrivacy(false)}/>}
    </>
  );

  // ── Landing Page ──────────────────────────────────────────────────────────
  if (showLanding) return (
    <>
      <LandingPage
        onEnterApp={() => {
          if (session) { setDemoMode(false); setShowLanding(false); }
          else { setShowLanding(false); setShowAuth(true); }
        }}
        onDemo={() => { setDemoMode(true); setShowLanding(false); }}
        onShowTerms={() => setShowTerms(true)}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
      {termsModals}
    </>
  );

  // ── Auth Screen ───────────────────────────────────────────────────────────
  if (showAuth) return (
    <>
      <AuthScreen
        onAuth={s => { setSession(s); setDemoMode(false); setShowAuth(false); }}
        onDemo={() => { setDemoMode(true); setShowAuth(false); }}
        onShowTerms={() => setShowTerms(true)}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
      {termsModals}
    </>
  );

  // ── Password Reset ────────────────────────────────────────────────────────
  if (showReset) return (
    <ResetPassword onDone={() => { setShowReset(false); setShowAuth(true); }}/>
  );

  // ── Data Loading ──────────────────────────────────────────────────────────
  if (!demoMode && session && db.loading) return (
    <div style={{minHeight:'100vh',background:'#0e1117',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
        <path d="M10 48 L16 24 L28 36 L32 16 L36 36 L48 24 L54 48 Z" fill="#22c55e"/>
        <rect x="10" y="50" width="44" height="6" rx="3" fill="#22c55e"/>
        <circle cx="32" cy="13" r="4" fill="#86efac"/>
      </svg>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:'#fff'}}>Loading your data...</div>
      <div style={{fontSize:13,color:'#484f58'}}>Connecting to database</div>
    </div>
  );

  // ── Portal Selection ──────────────────────────────────────────────────────
  if (!portal) return (
    <div className="portal">
      <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:10}}>
        <Ic n="crown" s={36} c="#22c55e"/>
        <div className="p-logo">Rent Lord</div>
        <div className="p-tag">Property Management Platform</div>
        {demoMode && <div style={{background:'rgba(210,153,34,.1)',border:'1px solid rgba(210,153,34,.25)',borderRadius:20,padding:'4px 14px',fontSize:11,fontWeight:700,color:'#d29922',letterSpacing:1}}>DEMO MODE</div>}
      </div>
      <div className="p-rule"/>
      <div className="pcards">
        <div className="pcard" onClick={()=>go('ll')}>
          <div className="pico pico-l"><Ic n="shield" s={28} c="#22c55e"/></div>
          <h3>Landlord Portal</h3>
          <p>Full property management — tracker, tenants, leases, financials, messages, documents, and more.</p>
          <button className="pbtn pbtn-l">Enter as Owner</button>
        </div>
        <div className="pcard" onClick={()=>setPortal('sel')}>
          <div className="pico pico-t"><Ic n="home" s={28} c="#4ade80"/></div>
          <h3>Tenant Portal</h3>
          <p>Pay rent, submit maintenance, message your landlord, and view your documents.</p>
          <button className="pbtn pbtn-t">Enter as Tenant</button>
        </div>
      </div>
    </div>
  );

  // ── Tenant Selector ───────────────────────────────────────────────────────
  if (portal === 'sel') return (
    <div className="portal">
      <div className="ts-ov" onClick={()=>setPortal(null)}>
        <div className="ts-box" onClick={e=>e.stopPropagation()}>
          <h2>Select Your Account</h2>
          <p>Choose your name to continue.</p>
          <div className="ts-list">
            {TENANTS.length === 0
              ? <div style={{textAlign:'center',padding:'32px 20px',color:'#484f58',fontSize:13}}>No tenants yet — add tenants from the Landlord portal first.</div>
              : TENANTS.map(t => {
                  const nm  = t.full_name || t.name || '';
                  const av  = t.avatar_initials || t.av || nm.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
                  const unit= t.unit || t.u || '—';
                  return (
                    <div className="ts-item" key={t.id} onClick={()=>{ setTenant(t); go('t'); }}>
                      <div className="ts-av">{av}</div>
                      <div>
                        <div style={{fontWeight:600,fontSize:14,color:'#fff'}}>{nm}</div>
                        <div style={{fontSize:12,color:'#8b949e'}}>Unit {unit}</div>
                      </div>
                    </div>
                  );
                })
            }
          </div>
          <button className="btn btn-gh" style={{marginTop:12,width:'100%',justifyContent:'center'}} onClick={()=>setPortal(null)}>← Back</button>
        </div>
      </div>
    </div>
  );

  // ── Main App ──────────────────────────────────────────────────────────────
  return (
    <>
      <InstallPrompt/>
      {termsModals}
      {showAddProp && <AddPropertyModal onSave={handleSaveProperty} onClose={()=>{setShowAddProp(false);setEditProp(null);}} existing={editProp}/>}
      {showAddTen  && <AddTenantModal  onSave={handleSaveTenant}   onClose={()=>{setShowAddTen(false);setEditTen(null);}}   existing={editTen} properties={PROPS}/>}
      {payMod && <PayModal  t={payMod}  addPayment={demoMode ? undefined : db.addPayment}  onClose={()=>setPayMod(null)}  onOk={payment=>{ db.reload && db.reload(); if(payMod?.email && !demoMode) sendPaymentReceipt({ tenantEmail:payMod.email, tenantName:payMod.full_name||payMod.name, payment }); setPayMod(null); }}/>}
      {mxMod  && <MxModal   t={tenant}  onClose={()=>setMxMod(false)}  onOk={()=>{ db.reload && db.reload(); setMxMod(false); }}/>}
      {sbOpen && <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:99}} onClick={()=>setSbOpen(false)}/>}

      <div className="app">
        {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
        <aside className={'sb'+(sbOpen?' sb-open':'')}>
          <div className="sb-top">
            <div className="sb-brand"><Ic n="crown" s={18} c="#22c55e"/><span className="sb-name">Rent Lord</span></div>
            <div className="sb-sub">Property Management</div>
            <div style={{display:'flex',alignItems:'center',gap:7,marginTop:8,flexWrap:'wrap'}}>
              <span className={'pill '+(isLL?'pill-l':'pill-t')}>{isLL?'Owner Portal':'Tenant Portal'}</span>
              {isLL && <span className={'tier-badge tier-'+tier}>{tierInfo.name}</span>}
              {demoMode && <span style={{background:'rgba(210,153,34,.1)',border:'1px solid rgba(210,153,34,.2)',borderRadius:10,padding:'2px 8px',fontSize:9,fontWeight:700,color:'#d29922'}}>DEMO</span>}
            </div>
          </div>

          <nav className="sb-nav">
            {nav.map(n => {
              const locked = isLL && n.pro && !isPro;
              return (
                <button key={n.id}
                  className={'nb'+(tab===n.id?' nb-on':'')}
                  onClick={()=>{ setTab(n.id); setSbOpen(false); }}
                  style={{opacity:locked?.5:1,position:'relative'}}>
                  <Ic n={n.ic} s={14} c={tab===n.id?'#4ade80':'#8b949e'}/>
                  {n.l}
                  {n.id==='maint' && isLL && omx>0  && <span className="nb-dot">{omx}</span>}
                  {n.id==='msgs'  && unread>0         && <span className="nb-dotg">{unread}</span>}
                  {locked && <span style={{marginLeft:'auto',fontSize:9,color:'#484f58',background:'#21262d',padding:'1px 6px',borderRadius:10,fontWeight:700}}>PRO</span>}
                </button>
              );
            })}
          </nav>

          <div className="sb-foot">
            {isLL && !isPro && (
              <div style={{padding:'10px 11px',background:'rgba(34,197,94,.06)',border:'1px solid rgba(34,197,94,.15)',borderRadius:8,marginBottom:7}}>
                <div style={{fontSize:11,fontWeight:600,color:'#4ade80',marginBottom:3}}>🔓 Upgrade to Pro</div>
                <div style={{fontSize:10,color:'#8b949e',marginBottom:8}}>Unlock 6 more powerful features for $49/mo</div>
                <button className="btn btn-g" style={{width:'100%',justifyContent:'center',padding:'7px',fontSize:11}} onClick={()=>setTab('pricing')}>
                  <Ic n="crown" s={12}/>View Plans
                </button>
              </div>
            )}
            <div className="uchip">
              <div className="uav" style={demoMode?{background:'linear-gradient(135deg,#d29922,#f5c842)',color:'#0e1117'}:{}}>
                {isLL ? (demoMode ? 'D' : (session?.user?.user_metadata?.full_name?.[0] || session?.user?.email?.[0] || 'L').toUpperCase()) : (tenant?.avatar_initials||tenant?.av||'T')}
              </div>
              <div>
                <div className="un">
                  {isLL ? (demoMode ? 'Demo Landlord' : (session?.user?.user_metadata?.full_name || session?.user?.email || 'Landlord')) : (tenant?.full_name||tenant?.name||'Tenant')}
                </div>
                <div className="ur">
                  {isLL ? (demoMode ? 'Demo Mode' : tierInfo.name+' Plan') : 'Unit '+(tenant?.unit||tenant?.u||'—')}
                </div>
              </div>
            </div>
            <button className="swb" onClick={()=>{ setPortal(null); setSbOpen(false); }}><Ic n="out" s={13} c="#8b949e"/>Switch Portal</button>
            <button className="swb" onClick={()=>{ setShowLanding(true); setPortal(null); setSbOpen(false); }} style={{borderColor:'rgba(34,197,94,.2)',color:'#4ade80'}}><Ic n="home" s={13} c="#4ade80"/>Back to Website</button>
            <button className="swb" onClick={handleSignOut} style={{borderColor:'rgba(248,81,73,.2)',color:'#f85149'}}><Ic n="out" s={13} c="#f85149"/>{demoMode?'Exit Demo':'Sign Out'}</button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
        <div className="main">
          <header className="topbar">
            <button className="mbtn" onClick={()=>setSbOpen(!sbOpen)}><Ic n="menu" s={22} c="#8b949e"/></button>
            <h1 className="topbar-title">{TITLES[tab]||tab}</h1>
            {demoMode && (
              <div style={{display:'flex',alignItems:'center',gap:6,background:'rgba(210,153,34,.1)',border:'1px solid rgba(210,153,34,.25)',borderRadius:20,padding:'3px 10px',flexShrink:0}}>
                <div style={{width:6,height:6,borderRadius:'50%',background:'#d29922'}}/>
                <span style={{fontSize:11,fontWeight:700,color:'#d29922',letterSpacing:1}}>DEMO</span>
              </div>
            )}
            {isLL && tab==='maint' && omx>0 && (
              <div style={{background:'rgba(248,81,73,.12)',color:'#f85149',border:'1px solid rgba(248,81,73,.25)',padding:'4px 12px',borderRadius:20,fontSize:12,fontWeight:600}}>
                {omx} open
              </div>
            )}
            {!isLL && (tab==='home'||tab==='pays') && (
              <button className="btn btn-g" style={{padding:'7px 15px',fontSize:13}} onClick={()=>setPayMod(tenant)}>
                <Ic n="dollar" s={13}/>Pay Rent
              </button>
            )}
          </header>

          <div className="pg">
            {/* ── LANDLORD VIEWS ────────────────────────────────────────── */}
            {isLL && <>
              {tab==='dash'     && <LLDash setTab={setTab} pays={PAYS} mx={MX} tenants={TENANTS} leases={LEASES}/>}
              {tab==='props'    && (gateCheck('props')
                ? <ProGate feature="Property Tracker" onUpgrade={()=>setTab('pricing')}/>
                : <div>
                    <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
                      <button className="btn btn-g" onClick={()=>{ setShowAddProp(true); setEditProp(null); }}>
                        <Ic n="plus" s={14}/>Add Property
                      </button>
                    </div>
                    <PropertyTracker props={PROPS} mx={MX} tenants={TENANTS} onEdit={p=>{ setEditProp(p); setShowAddProp(true); }}/>
                  </div>
              )}
              {tab==='tenants'  && <div>
                <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
                  <button className="btn btn-g" onClick={()=>{ setShowAddTen(true); setEditTen(null); }}>
                    <Ic n="plus" s={14}/>Add Tenant
                  </button>
                </div>
                <LLTenants pays={PAYS} setPay={setPayMod} tenants={TENANTS} leases={LEASES} onEdit={t=>{ setEditTen(t); setShowAddTen(true); }}/>
              </div>}
              {tab==='leases'   && <LLLeases leases={LEASES} tenants={TENANTS}/>}
              {tab==='maint'    && <LLMaint  mx={MX} onStatusUpdate={handleMxStatusUpdate}/>}
              {tab==='fin'      && <LLFin    pays={PAYS}/>}
              {tab==='pricing_intel' && (gateCheck('pricing_intel')
                ? <ProGate feature="Rent Pricing Intelligence" onUpgrade={()=>setTab('pricing')}/>
                : <RentPricing properties={PROPS.map(p => {
                    const pid    = String(p.id);
                    const lease  = LEASES.find(l => String(l.property_id) === pid || l.p === p.name || l.property === p.name);
                    const tenant = TENANTS.find(t => String(t.property_id) === pid || t.p === p.name || t.property === p.name);
                    const rent   = lease?.monthly_rent || lease?.rent || tenant?.monthly_rent || tenant?.rent || 0;
                    return { ...p, monthly_rent: rent };
                  })}/>
              )}
              {tab==='expense'  && <ExpenseTracker expenses={EXPENSES} onAdd={demoMode ? undefined : db.addExpense} onDelete={demoMode ? undefined : db.deleteExpense}/>}
              {tab==='msgs'     && <Messaging msgs={MSGS} addMessage={demoMode ? undefined : db.addMessage} isLL={true} activeTenant={null} tenants={TENANTS}/>}
              {tab==='broadcast'&& (gateCheck('broadcast')
                ? <ProGate feature="Broadcast Messaging" onUpgrade={()=>setTab('pricing')}/>
                : <BroadcastMsg tenants={TENANTS} addMessage={demoMode ? undefined : db.addMessage}/>
              )}
              {tab==='docs'     && <Documents docs={DOCS} addDocument={demoMode ? undefined : db.addDocument} deleteDocument={demoMode ? undefined : db.deleteDocument} isLL={true} activeTenant={null}/>}
              {tab==='fees'     && <LateFees pays={PAYS} tenants={TENANTS} leases={LEASES} lateFees={LATE_FEES} addLateFee={demoMode ? undefined : db.addLateFee}/>}
              {tab==='receipts' && <Receipts     pays={PAYS} isLL={true} activeTenant={null} tenants={TENANTS} leases={LEASES}/>}
              {tab==='checks'   && <Checklists   isLL={true} activeTenant={null} tenants={TENANTS} checklists={CHECKS} addChecklist={demoMode ? undefined : db.addChecklist} updateChecklist={demoMode ? undefined : db.updateChecklist}/>}
              {tab==='vacancy'  && (gateCheck('vacancy')
                ? <ProGate feature="Vacancy Tracker" onUpgrade={()=>setTab('pricing')}/>
                : <VacancyTracker vacancies={VACS} onAdd={demoMode ? undefined : db.addVacancy} onUpdate={demoMode ? undefined : db.updateVacancy}/>
              )}
              {tab==='renewal'  && (gateCheck('renewal')
                ? <ProGate feature="Lease Renewal" onUpgrade={()=>setTab('pricing')}/>
                : <LeaseRenewal tenants={TENANTS} leases={LEASES}/>
              )}
              {tab==='ntv'      && (gateCheck('ntv')
                ? <ProGate feature="Notice to Vacate" onUpgrade={()=>setTab('pricing')}/>
                : <NoticeToVacate tenants={TENANTS} leases={LEASES}/>
              )}
              {tab==='pricing'  && <PricingPage currentTier={tier} onSelect={async t=>{ if(!demoMode) await db.updateSubscription(t); setTab('dash'); }}/>}
              {tab==='vendors'  && <VendorDirectory/>}
            </>}

            {/* ── TENANT VIEWS ──────────────────────────────────────────── */}
            {!isLL && tenant && <>
              {tab==='home'     && <THome    t={tenant} pays={PAYS} onPay={()=>setPayMod(tenant)} leases={LEASES}/>}
              {tab==='pays'     && <TPays    t={tenant} pays={PAYS} onPay={()=>setPayMod(tenant)}/>}
              {tab==='maint'    && <TMaint   t={tenant} mx={MX}    onNew={()=>setMxMod(true)}/>}
              {tab==='msgs'     && <Messaging msgs={MSGS} addMessage={demoMode ? undefined : db.addMessage} isLL={false} activeTenant={tenant} tenants={TENANTS}/>}
              {tab==='docs'     && <Documents docs={DOCS} addDocument={demoMode ? undefined : db.addDocument} deleteDocument={demoMode ? undefined : db.deleteDocument} isLL={false} activeTenant={tenant}/>}
              {tab==='receipts' && <Receipts  pays={PAYS} isLL={false} activeTenant={tenant} tenants={TENANTS} leases={LEASES}/>}
              {tab==='checks'   && <Checklists isLL={false} activeTenant={tenant} tenants={TENANTS} checklists={CHECKS} addChecklist={demoMode ? undefined : db.addChecklist} updateChecklist={demoMode ? undefined : db.updateChecklist}/>}
            </>}
          </div>
        </div>
      </div>
    </>
  );
}
